import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials, todayISO } from "@/lib/api";
import { toast } from "sonner";
import {
  ListChecks, Plus, Search, CheckCircle2, Circle, Pencil, Trash2, User, ArrowLeft,
  Save, Calendar, MessageSquare, Sparkles,
} from "lucide-react";

export const TIPOS_SOLICITUD = [
  "Constancia Laboral",
  "Constancia Salarial",
  "Carta de Despido con responsabilidad",
  "Carta de Despido sin responsabilidad",
  "Carta FCL",
  "Vacaciones",
  "Reenvío de Comprobante",
  "Carta de Renuncia",
  "Acción de Personal",
  "Otro",
];

// ============================================================================
// FORM (create / edit)
// ============================================================================
function SolicitudForm({ initial, empleados, onBack, onSaved }) {
  const isInitialOtro = !!(initial?.tipo && !TIPOS_SOLICITUD.includes(initial.tipo));
  const [tipo, setTipo] = useState(isInitialOtro ? "Otro" : (initial?.tipo || TIPOS_SOLICITUD[0]));
  const [tipoOtro, setTipoOtro] = useState(isInitialOtro ? initial.tipo : "");
  const [empleadoId, setEmpleadoId] = useState(initial?.empleado_id || "");
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [solicitadoPor, setSolicitadoPor] = useState(initial?.solicitado_por || "Patrono");
  const [fechaSolicitud, setFechaSolicitud] = useState(
    initial?.fecha_solicitud ? initial.fecha_solicitud.slice(0,10) : todayISO()
  );
  const [prioridad, setPrioridad] = useState(initial?.prioridad || "media");
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;
  const isOtro = tipo === "Otro";

  const guardar = async () => {
    const tipoFinal = isOtro ? tipoOtro.trim() : tipo;
    if (!tipoFinal) { toast.error("Indicá el tipo de solicitud"); return; }
    setSaving(true);
    try {
      const payload = {
        tipo: tipoFinal,
        empleado_id: empleadoId || null,
        descripcion: descripcion.trim(),
        solicitado_por: solicitadoPor.trim() || "Patrono",
        fecha_solicitud: fechaSolicitud,
        prioridad: prioridad,
      };
      if (isEdit) {
        await api.put(`/solicitudes/${initial.id}`, payload);
        toast.success("Solicitud actualizada");
      } else {
        await api.post("/solicitudes", payload);
        toast.success("Solicitud registrada");
      }
      onSaved?.();
    } catch {
      toast.error("No se pudo guardar la solicitud");
    } finally { setSaving(false); }
  };

  return (
    <>
      <header className="flex items-center gap-3 mb-6 fade-in-up">
        <button onClick={onBack} className="np-btn np-btn-ghost p-2" data-testid="sol-back"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl font-semibold leading-tight">{isEdit ? "Editar Solicitud" : "Nueva Solicitud"}</h1>
          <p className="text-sm text-secondary-c">Registrá lo que el patrono pidió generar.</p>
        </div>
      </header>

      <div className="np-card p-6 max-w-2xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">Tipo de solicitud *</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="np-input w-full" data-testid="sol-form-tipo">
              {TIPOS_SOLICITUD.map(t => <option key={t}>{t}</option>)}
            </select>
            {isOtro && (
              <input
                value={tipoOtro} onChange={e => setTipoOtro(e.target.value)}
                placeholder="Describe el tipo..."
                className="np-input w-full mt-2"
                data-testid="sol-form-tipo-otro"
              />
            )}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Fecha de solicitud</label>
            <input type="date" value={fechaSolicitud} onChange={e => setFechaSolicitud(e.target.value)}
              className="np-input w-full" data-testid="sol-form-fecha" />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1.5"><User size={14} className="inline mr-1 -mt-0.5" /> Trabajador (opcional)</label>
          <select value={empleadoId} onChange={e => setEmpleadoId(e.target.value)} className="np-input w-full" data-testid="sol-form-empleado">
            <option value="">— Sin trabajador asociado —</option>
            {empleados.filter(e => e.estado === "Activo").map(e => (
              <option key={e.id} value={e.id}>{e.nombre} {e.apellidos} — {e.cedula}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1.5">Solicitado por</label>
          <input value={solicitadoPor} onChange={e => setSolicitadoPor(e.target.value)} className="np-input w-full" data-testid="sol-form-por" placeholder="Patrono, Gerencia, RR.HH..." />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1.5">Prioridad</label>
          <div className="grid grid-cols-3 gap-2" data-testid="sol-form-prioridad">
            {[
              { v: "baja", label: "Baja", cls: "border-[#C8DACF] bg-[#E6EFEA] text-primary-color", ring: "ring-[#274735]" },
              { v: "media", label: "Media", cls: "border-[#F0CC8C] bg-[#FFF7E5] text-[#A67115]", ring: "ring-[#A67115]" },
              { v: "alta", label: "Alta", cls: "border-[#F2C0BC] bg-[#FDEDED] text-error", ring: "ring-error" },
            ].map(p => {
              const active = prioridad === p.v;
              return (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPrioridad(p.v)}
                  className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${p.cls} ${active ? `ring-2 ${p.ring}` : "opacity-60 hover:opacity-100"}`}
                  data-testid={`sol-prio-${p.v}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1.5"><MessageSquare size={14} className="inline mr-1 -mt-0.5" /> Descripción / notas</label>
          <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} className="np-input w-full min-h-[100px] resize-y" data-testid="sol-form-descripcion"
            placeholder="Detalles adicionales sobre lo solicitado..." />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onBack} className="np-btn np-btn-outline" data-testid="sol-form-cancel">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="np-btn np-btn-primary" data-testid="sol-form-save">
            <Save size={14} className="mr-1.5" /> {saving ? "Guardando..." : (isEdit ? "Guardar Cambios" : "Registrar Solicitud")}
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// LIST
// ============================================================================
function SolicitudesList({ items, empleados, onNew, onEdit, onToggle, onDelete }) {
  const [filtro, setFiltro] = useState("pendiente"); // pendiente | realizada | all
  const [q, setQ] = useState("");
  const empMap = useMemo(() => {
    const m = {}; empleados.forEach(e => { m[e.id] = `${e.nombre} ${e.apellidos || ""}`.trim(); }); return m;
  }, [empleados]);

  const filtered = useMemo(() => {
    let out = items;
    if (filtro !== "all") out = out.filter(i => i.estado === filtro);
    const s = q.trim().toLowerCase();
    if (s) out = out.filter(i =>
      (i.tipo || "").toLowerCase().includes(s) ||
      (empMap[i.empleado_id] || "").toLowerCase().includes(s) ||
      (i.descripcion || "").toLowerCase().includes(s)
    );
    return out;
  }, [items, filtro, q, empMap]);

  const counts = {
    pendiente: items.filter(i => i.estado === "pendiente").length,
    realizada: items.filter(i => i.estado === "realizada").length,
  };

  return (
    <>
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <ListChecks size={26} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Solicitudes</h1>
            <p className="text-sm text-secondary-c">Lo que el patrono pidió generar — pendientes y realizadas.</p>
          </div>
        </div>
        <button onClick={onNew} className="np-btn np-btn-primary" data-testid="sol-new">
          <Plus size={16} className="mr-1.5" /> Nueva Solicitud
        </button>
      </header>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="inline-flex p-1 rounded-xl bg-[#FAFAF7] border border-c">
          <TabBtn active={filtro === "pendiente"} onClick={() => setFiltro("pendiente")} testid="sol-tab-pendientes">
            Pendientes <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFF4E5] text-warning ml-1">{counts.pendiente}</span>
          </TabBtn>
          <TabBtn active={filtro === "realizada"} onClick={() => setFiltro("realizada")} testid="sol-tab-realizadas">
            Realizadas <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E6EFEA] text-primary-c ml-1">{counts.realizada}</span>
          </TabBtn>
          <TabBtn active={filtro === "all"} onClick={() => setFiltro("all")} testid="sol-tab-all">Todas</TabBtn>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-c bg-white">
          <Search size={14} className="text-secondary-c" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar..."
            className="bg-transparent outline-none text-sm py-1 w-48" data-testid="sol-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="np-card p-14 text-center text-secondary-c">
          <Sparkles size={36} className="mx-auto mb-3 opacity-60" />
          {filtro === "pendiente"
            ? "No hay solicitudes pendientes. Todo al día ✨"
            : "Sin solicitudes."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="sol-list">
          {filtered.map(it => {
            const empNombre = it.empleado_id ? empMap[it.empleado_id] : null;
            const isDone = it.estado === "realizada";
            return (
              <div key={it.id} className={`np-card p-4 flex items-start gap-3 ${isDone ? "opacity-70" : ""}`} data-testid={`sol-row-${it.id}`}>
                <button
                  onClick={() => onToggle(it)}
                  className="mt-0.5 flex-shrink-0 transition-transform hover:scale-110"
                  title={isDone ? "Marcar como pendiente" : "Marcar como realizada"}
                  data-testid={`sol-toggle-${it.id}`}
                >
                  {isDone
                    ? <CheckCircle2 size={22} className="text-success" />
                    : <Circle size={22} className="text-secondary-c hover:text-primary-c" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className={`font-medium flex items-center gap-2 flex-wrap ${isDone ? "line-through text-secondary-c" : ""}`}>
                        {it.tipo}
                        {it.prioridad && <PrioridadBadge value={it.prioridad} />}
                      </div>
                      {empNombre && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-secondary-c">
                          <span className="w-5 h-5 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-[10px] font-semibold">
                            {initials(empNombre)}
                          </span>
                          {empNombre}
                        </div>
                      )}
                      {it.descripcion && <div className="text-xs text-secondary-c mt-1.5 line-clamp-2">{it.descripcion}</div>}
                    </div>
                    <div className="text-right text-xs text-secondary-c flex-shrink-0">
                      <div className="flex items-center gap-1"><Calendar size={11} /> {fmtFecha(it.fecha_solicitud)}</div>
                      <div className="mt-0.5">Por <strong className="text-primary-c">{it.solicitado_por || "Patrono"}</strong></div>
                      {isDone && it.fecha_realizada && (
                        <div className="mt-0.5 text-success">Realizada {fmtFecha(it.fecha_realizada)}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => onEdit(it)} className="np-btn np-btn-ghost p-1.5" title="Editar" data-testid={`sol-edit-${it.id}`}><Pencil size={13} /></button>
                  <button onClick={() => onDelete(it)} className="np-btn np-btn-ghost p-1.5 text-error hover:bg-[#FDEDED]" title="Eliminar" data-testid={`sol-delete-${it.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-white shadow-sm text-primary-c" : "text-secondary-c hover:text-primary-c"}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const PrioridadBadge = ({ value }) => {
  const v = String(value || "").toLowerCase();
  const cfg = {
    baja:  { label: "Baja",  cls: "bg-[#E6EFEA] text-primary-color border-[#C8DACF]" },
    media: { label: "Media", cls: "bg-[#FFF7E5] text-[#A67115] border-[#F0CC8C]" },
    alta:  { label: "Alta",  cls: "bg-[#FDEDED] text-error border-[#F2C0BC]" },
  }[v];
  if (!cfg) return null;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`} data-testid={`sol-prio-badge-${v}`}>
      {cfg.label}
    </span>
  );
};

// ============================================================================
// ROOT
// ============================================================================
export default function Solicitudes() {
  const [mode, setMode] = useState("list");
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [current, setCurrent] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = async () => {
    const { data } = await api.get("/solicitudes");
    setItems(data || []);
  };

  useEffect(() => {
    (async () => {
      const [s, e] = await Promise.all([api.get("/solicitudes"), api.get("/empleados")]);
      setItems(s.data || []);
      setEmpleados(e.data || []);
    })();
    // Mount-only intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (it) => {
    const nuevo = it.estado === "pendiente" ? "realizada" : "pendiente";
    try {
      await api.put(`/solicitudes/${it.id}`, { estado: nuevo });
      toast.success(nuevo === "realizada" ? "Marcada como realizada" : "Movida a pendientes");
      await reload();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    await api.delete(`/solicitudes/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Solicitud eliminada");
    await reload();
  };

  return (
    <Layout>
      {mode === "list" ? (
        <SolicitudesList
          items={items} empleados={empleados}
          onNew={() => { setCurrent(null); setMode("form"); }}
          onEdit={(it) => { setCurrent(it); setMode("form"); }}
          onToggle={toggle}
          onDelete={(it) => setConfirmDel(it)}
        />
      ) : (
        <SolicitudForm
          initial={current} empleados={empleados}
          onBack={() => setMode("list")}
          onSaved={async () => { await reload(); setMode("list"); }}
        />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="np-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Eliminar solicitud</h3>
            <p className="text-sm text-secondary-c mb-4">Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="np-btn np-btn-outline" data-testid="sol-confirm-cancel">Cancelar</button>
              <button onClick={doDelete} className="np-btn np-btn-primary !bg-error" data-testid="sol-confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
